// Times the game sends: "Y-m-d H:i:s +0000" strings (Time::convertToUTC), unix seconds (server_time) and ms stamps (*_ms).

/** "2026-10-07 22:41:43 +0000" → ms since epoch (NaN when unreadable). */
export function parseGameTime(s: string | null | undefined): number {
  if (!s) return NaN;
  const m = /^(\d{4})-(\d{2})-(\d{2})[ T](\d{2}):(\d{2}):(\d{2})(?:\.\d+)?\s*(?:([+-])(\d{2}):?(\d{2})|Z)?$/.exec(s.trim());
  if (!m) return NaN;
  const [, Y, Mo, D, h, mi, se, sign, oh, om] = m;
  let t = Date.UTC(+Y, +Mo - 1, +D, +h, +mi, +se);
  if (sign) {
    const off = (+oh * 60 + +om) * 60e3;
    t += sign === "+" ? -off : off;
  }
  return t;
}

const pad = (n: number) => (n < 10 ? "0" : "") + n;

/** A duration as the game shows it: "2:00:29", "0:00:09", or with days "1d 02:14:00". */
export function fmtDuration(ms: number, opts: { compact?: boolean } = {}): string {
  if (!Number.isFinite(ms)) return "—";
  const total = Math.max(0, Math.round(ms / 1000));
  const d = Math.floor(total / 86400);
  const h = Math.floor((total % 86400) / 3600);
  const m = Math.floor((total % 3600) / 60);
  const s = total % 60;
  if (opts.compact) {
    if (d > 0) return `${d}d ${h}h`;
    if (h > 0) return `${h}h ${pad(m)}m`;
    if (m > 0) return `${m}m ${pad(s)}s`;
    return `${s}s`;
  }
  const hms = `${d > 0 ? pad(h) : h}:${pad(m)}:${pad(s)}`;
  return d > 0 ? `${d}d ${hms}` : hms;
}

/** "about 9 h" / "1h 20m" style for prose. */
export function fmtAgo(ms: number): string {
  const total = Math.max(0, Math.round(ms / 1000));
  if (total < 60) return "just now";
  const m = Math.floor(total / 60);
  if (m < 60) return `${m}m ago`;
  const h = Math.floor(m / 60);
  if (h < 24) return `${h}h ${pad(m % 60)}m ago`;
  const d = Math.floor(h / 24);
  return `${d}d ago`;
}

/** Clock time "23:28" (UTC, 24 h) for a ms stamp; with seconds when asked. Ben, Oct 13 2026: every clock in Valor Web is UTC,
 *  the same for every player wherever they are. */
export function fmtClock(ms: number, seconds = false): string {
  if (!Number.isFinite(ms)) return "—";
  const d = new Date(ms);
  return `${pad(d.getUTCHours())}:${pad(d.getUTCMinutes())}${seconds ? ":" + pad(d.getUTCSeconds()) : ""}`;
}

/** "Mar 2025", "Sep 18" */
export function fmtDate(ms: number, withYear = false): string {
  if (!Number.isFinite(ms)) return "—";
  const d = new Date(ms);
  const mon = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"][d.getUTCMonth()];
  return withYear ? `${mon} ${d.getUTCFullYear()}` : `${mon} ${d.getUTCDate()}`;
}

/** The game's duration strings: "H:MM:SS" / "HH:MM:SS" (buildings, research, queues, arrival_in) → ms. NaN when unreadable. */
export function parseHMS(s: string | null | undefined): number {
  if (!s) return NaN;
  const m = /^\s*(\d+):(\d{1,2}):(\d{1,2})\s*$/.exec(s);
  if (!m) return NaN;
  return (+m[1] * 3600 + +m[2] * 60 + +m[3]) * 1000;
}

/** Troop and scholar recruit times come as MINUTES:SECONDS ("4:37", "180:00") → ms. */
export function parseMinSec(s: string | null | undefined): number {
  if (!s) return NaN;
  const m = /^\s*(\d+):(\d{1,2})\s*$/.exec(s);
  if (!m) return NaN;
  return (+m[1] * 60 + +m[2]) * 1000;
}

/** "10d 8h", "9h 12m", "35m", "about 9 h" style for prose spans. */
export function fmtSpan(ms: number): string {
  if (!Number.isFinite(ms)) return "—";
  const total = Math.max(0, Math.round(ms / 1000));
  const d = Math.floor(total / 86400);
  const h = Math.floor((total % 86400) / 3600);
  const m = Math.floor((total % 3600) / 60);
  if (d > 0) return `${d}d ${h}h`;
  if (h > 0) return m ? `${h}h ${m}m` : `${h}h`;
  if (m > 0) return `${m}m`;
  return `${total}s`;
}

/** The UTC day of a ms stamp, as a number of days since 1970 (for "today" / "yesterday" on the UTC clock). */
export const utcDay = (ms: number) => Math.floor(ms / 86400_000);

/** "10/13/2026 12:19:25" (UTC), the live game's date stamp. */
export function fmtStampUTC(ms: number): string {
  if (!Number.isFinite(ms)) return "—";
  const d = new Date(ms);
  return `${d.getUTCMonth() + 1}/${d.getUTCDate()}/${d.getUTCFullYear()} ${fmtClock(ms, true)}`;
}

/** "23:28" today, "06:02 tomorrow", "Sep 18 06:02" further out (UTC). */
export function fmtWhen(ms: number, now: number): string {
  if (!Number.isFinite(ms)) return "—";
  const sameDay = utcDay(ms) === utcDay(now);
  const tomorrow = utcDay(ms) === utcDay(now) + 1;
  const hm = fmtClock(ms);
  if (sameDay) return hm;
  if (tomorrow) return `${hm} tomorrow`;
  return `${fmtDate(ms)} ${hm}`;
}
